import useAuthState from "@/composables/state/authState";
import usePlatformScreenState from "@/composables/state/platformScreenState";
import AppConfig from "@/config";
import axios, { HttpStatusCode } from "axios";
import { hasUrlMatchingPath } from "@/util/common";
import useToastState from "@/composables/state/toastState";
import useTrackingState from "@/composables/state/trackingState";
import i18n from "@/i18n";
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import type { z } from "zod";
import * as Sentry from "@sentry/vue";
import { useAnalyticsState } from "@/composables/state/analyticsState";
import { AnalyticsEvent } from "@/enums/analytics";
import { LATEST_API_VERSION } from "@lendflow/utils/constants";

/**
 * Safely validates the data against the schema and logs the errors if any
 * @param schema The schema to validate the data with
 * @param data The data to validate
 * @returns The validated data or the original data if validation fails
 */
export function validateWithSchema<TSchema extends z.ZodSchema, TData>(
  schema: TSchema,
  data: TData
): z.infer<TSchema> {
  const schemaResult = schema.safeParse(data);
  if (schemaResult.success) {
    return schemaResult.data;
  } else {
    if (AppConfig.isProduction) {
      Sentry.captureException(schemaResult.error);
    } else {
      console.error("Schema validation failed", schemaResult.error);
    }
    return data as z.infer<TSchema>;
  }
}

export abstract class BaseHttpService {
  private instance: AxiosInstance;

  /**
   * @description Prevent toast messages from appearing to the user if the request fails.
   */
  public silentErrors?: boolean = false;

  abstract screenLoadingPaths: string[];

  constructor() {
    this.instance = axios.create({
      baseURL: AppConfig.apiBaseUrl,
      headers: this.headers,
    });

    const { showLoadingFullscreen } = usePlatformScreenState();
    const { setAuthToken, setLoggedInUser } = useAuthState();

    this.instance.interceptors.request.use((config) => {
      if (this.hasMatchingLoadingPath(config.url || "")) {
        showLoadingFullscreen.value = true;
      }

      return config;
    });

    const { trackingQueryParams } = useTrackingState();

    this.instance.interceptors.request.use((config) => {
      if (Object.keys(trackingQueryParams.value).length) {
        config.params = { ...trackingQueryParams.value, ...config.params };
      }

      return config;
    });

    this.instance.interceptors.response.use(
      (response) => {
        if (this.hasMatchingLoadingPath(response.config.url || "") && showLoadingFullscreen.value) {
          showLoadingFullscreen.value = false;
        }

        return response;
      },
      (error) => {
        const response = (error as AxiosError<{ message?: string }>).response;
        if (showLoadingFullscreen.value) {
          showLoadingFullscreen.value = false;
        }
        const status = response?.status;
        let message = response?.data?.message;
        const isServerError = status !== undefined && status >= 500;

        if (isServerError) {
          const t = i18n.global.t;
          message = t("common.apiErrorOccurred");
        } else if (status === HttpStatusCode.Unauthorized) {
          setAuthToken(null);
          setLoggedInUser(null);
        }

        if (message && !this.silentErrors) {
          const { selectedTabId, currentScreenComponent } = usePlatformScreenState();
          const { trackEvent } = useAnalyticsState();

          trackEvent(AnalyticsEvent.ApiErrorDisplayed, {
            endpoint: error.config?.url ?? "unknown",
            error_code: error.response?.status ?? "network_error",
            current_tab: selectedTabId.value ?? "unknown",
            current_screen: currentScreenComponent.value?.name ?? "unknown",
          });

          const toast = useToastState();
          const messagePayload = {
            title: message,
            type: "error",
          } as const;
          toast.value.addMessage(messagePayload);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * The computed headers
   * @returns The headers object
   */
  private get headers(): Record<string, string> {
    const { authToken } = useAuthState();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "Lendflow-Api-Version": LATEST_API_VERSION,
    };

    if (authToken.value) {
      headers["Authorization"] = `Bearer ${authToken.value}`;
    }

    if (AppConfig.token) {
      headers["Borrower-Platform-Client-Token"] = AppConfig.token;
    }

    return headers;
  }

  /**
   * @param url string
   * @returns A boolean indicating if the url matches any of the screen loading paths
   */
  private hasMatchingLoadingPath(url: string) {
    return hasUrlMatchingPath(url, this.screenLoadingPaths);
  }

  protected async get<T>(
    url: string,
    config?: Pick<AxiosRequestConfig, "params" | "responseType">
  ): Promise<T> {
    const response = await this.instance.get<T>(url, config);
    return response.data;
  }

  protected async post<T>(
    url: string,
    data?: Record<string, unknown> | FormData,
    config?: Pick<AxiosRequestConfig, "headers" | "onUploadProgress">
  ): Promise<T> {
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  protected async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.instance.put<T>(url, data);
    return response.data;
  }

  protected async delete<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.instance.delete(url, { data });
    return response.data;
  }
}
